import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getHotCards, type HotCardItem } from '../api/cards';
import { hotCardsMock } from '../mocks/hotCards';

export default function HotCards() {
  const navigate = useNavigate();
  const [cards, setCards] = useState<HotCardItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // 防止 StrictMode 下重复调用
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchHotCards = async () => {
      try {
        setIsLoading(true);
        const response = await getHotCards(8, 7);
        if (response.code === 200 && response.data.length > 0) {
          setCards(response.data);
        } else {
          // 如果没有数据，使用 mock 数据作为后备
          setCards(hotCardsMock.map((m) => ({
            id: m.id,
            name: m.name,
            image: m.image,
            number: m.number || '',
            transactionCount: 0,
            avgPrice: 0,
            currency: 'JPY',
          })));
        }
      } catch (err) {
        console.error('获取热门卡片失败:', err);
        // 使用 mock 数据作为后备
        setCards(hotCardsMock.map((m) => ({
          id: m.id,
          name: m.name,
          image: m.image,
          number: m.number || '',
          transactionCount: 0,
          avgPrice: 0,
          currency: 'JPY',
        })));
      } finally {
        setIsLoading(false);
      }
    };

    fetchHotCards();
  }, []);

  const handleCardClick = (card: HotCardItem) => {
    navigate(`/card/${card.id}`, {
      state: {
        card: {
          uid: card.id,
          name: card.name,
          number: card.number,
          image_url_at_self_platform: card.image,
        },
      },
    });
  };

  if (isLoading) {
    return (
      <div>
        <p className="eyebrow mb-2 text-cyan">Market discovery</p>
        <h2 className="mb-5 text-2xl font-semibold tracking-[-0.04em] text-primary">Trending Pokémon cards</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 sm:gap-5">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="animate-pulse overflow-hidden rounded-[20px] border border-border bg-white">
              <div className="aspect-[63/88] bg-gray-200" />
              <div className="p-3">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <p className="eyebrow text-cyan">Market discovery</p>
          <h2 className="mt-2 text-2xl font-semibold tracking-[-0.04em] text-primary">Trending Pokémon cards</h2>
        </div>
        <span className="hidden text-xs text-secondary sm:block">Ranked by sold activity · last 7 days</span>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-5 md:grid-cols-4">
        {cards.map((card) => (
          <button
            key={card.id}
            onClick={() => handleCardClick(card)}
            className="group overflow-hidden rounded-[20px] border border-border bg-white text-left transition-all hover:-translate-y-1 hover:border-cyan/40 hover:shadow-panel"
          >
            {/* 卡片图片 */}
            <div className="relative aspect-[63/88] overflow-hidden bg-gradient-to-b from-white to-background-secondary p-2 sm:p-3">
              {card.image ? (
                <img
                  src={card.image}
                  alt={card.name}
                  className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-[1.025]"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-secondary-light">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              )}
              {/* 成交量标签 */}
              {card.transactionCount > 0 && (
                <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded">
                  {card.transactionCount} sales
                </div>
              )}
            </div>

            {/* 卡片信息 */}
            <div className="border-t border-border/70 p-3.5">
              <h3 className="truncate text-sm font-bold text-primary">{card.name}</h3>
              {card.number && (
                <p className="text-xs text-secondary-light mt-1 truncate">{card.number}</p>
              )}
              <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-secondary-light">View market evidence →</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
